import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { api, type SemesterStatus } from "../lib/api";
import { PREMIUM_ROUTE } from "../lib/constants";
import { saveRedirectTarget } from "../lib/redirect";
import { invalidateStudySubjectsCache } from "../lib/useStudySubjects";

export type OpenUpgradeOptions = {
  semester?: number | null;
  stream?: string | null;
};

type StudentContextValue = {
  isPremium: boolean;
  setIsPremium: (v: boolean) => void;
  showUpgrade: boolean;
  openUpgrade: (options?: OpenUpgradeOptions) => void;
  closeUpgrade: () => void;
  isAdminPreview: boolean;
  semesterStatuses: SemesterStatus[];
  refreshSemesterStatus: () => Promise<SemesterStatus[]>;
  getSemesterStatus: (semesterNumber: number) => SemesterStatus | undefined;
  hasPendingRequest: (semesterNumber?: number | null) => boolean;
};

const StudentContext = createContext<StudentContextValue | null>(null);

export function StudentProvider({ children }: { children: ReactNode }) {
  const { role, tokens, isAuthenticated } = useAuth();
  const isAdminPreview = role === "admin";
  const [isPremiumFlag, setIsPremium] = useState(false);
  const [semesterStatuses, setSemesterStatuses] = useState<SemesterStatus[]>([]);
  const navigate = useNavigate();

  const refreshSemesterStatus = useCallback(async (): Promise<SemesterStatus[]> => {
    const token = tokens?.accessToken;
    if (!token) {
      setSemesterStatuses([]);
      return [];
    }
    try {
      const statuses = await api.payments.semesterStatus(token);
      setSemesterStatuses((prev) => {
        // Detect if any semester transitioned from locked to unlocked
        const wasLocked = prev.some((p) => !p.unlocked);
        const nowUnlocked = statuses.some((s) => s.unlocked);
        if (wasLocked && nowUnlocked) {
          invalidateStudySubjectsCache();
        }
        return statuses;
      });
      return statuses;
    } catch {
      return [];
    }
  }, [tokens?.accessToken]);

  // Initial fetch
  useEffect(() => {
    if (tokens?.accessToken) {
      refreshSemesterStatus();
    }
  }, [tokens?.accessToken, refreshSemesterStatus]);

  // Sync on window focus so approvals reflect immediately without manual reload
  useEffect(() => {
    const handleFocus = () => {
      if (tokens?.accessToken) {
        refreshSemesterStatus();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [tokens?.accessToken, refreshSemesterStatus]);

  // Poll periodically if any semester has a request awaiting review
  useEffect(() => {
    const hasPending = semesterStatuses.some(
      (s) => s.hasPendingRequest || s.has_pending_request || s.pendingRequestStatus === "pending" || s.pendingRequestStatus === "pending_review"
    );
    if (!hasPending || !tokens?.accessToken) return;

    const interval = setInterval(() => {
      refreshSemesterStatus();
    }, 12000);

    return () => clearInterval(interval);
  }, [semesterStatuses, tokens?.accessToken, refreshSemesterStatus]);

  const getSemesterStatus = useCallback(
    (sem: number) => semesterStatuses.find((s) => s.semester === sem),
    [semesterStatuses]
  );

  const hasPendingRequest = useCallback(
    (sem?: number | null) => {
      if (sem) {
        const s = semesterStatuses.find((item) => item.semester === sem);
        return Boolean(s?.hasPendingRequest || s?.has_pending_request);
      }
      return semesterStatuses.some((s) => s.hasPendingRequest || s.has_pending_request);
    },
    [semesterStatuses]
  );

  const openUpgrade = useCallback(
    (options?: OpenUpgradeOptions) => {
      const semParam = options?.semester;
      const streamParam = options?.stream;

      const buildTargetUrl = (targetSem?: number | null, targetStream?: string | null) => {
        const params = new URLSearchParams();
        if (targetSem) params.set("semester", String(targetSem));
        if (targetStream && targetStream !== "all") params.set("stream", targetStream);
        const qs = params.toString();
        return `${PREMIUM_ROUTE}${qs ? `?${qs}` : ""}`;
      };

      if (!isAuthenticated) {
        const dest = buildTargetUrl(semParam, streamParam);
        saveRedirectTarget(dest);
        navigate(`/auth/login?redirect=${encodeURIComponent(dest)}`);
        return;
      }

      if (semParam && streamParam) {
        navigate(buildTargetUrl(semParam, streamParam));
        return;
      }

      // If called without complete context (e.g. from header/footer/dashboard),
      // determine student's profile stream and active/locked semester from semester-status.
      const token = tokens?.accessToken;
      if (token) {
        Promise.all([
          api.profile(token).catch(() => null),
          api.payments.semesterStatus(token).catch(() => null),
        ])
          .then(([prof, semStatuses]) => {
            let chosenStream = streamParam;
            if (!chosenStream && prof?.streamName) {
              const lower = prof.streamName.toLowerCase();
              chosenStream = lower.includes("natural") ? "natural" : (lower.includes("social") ? "social" : "common");
            }

            let chosenSem = semParam;
            if (!chosenSem && Array.isArray(semStatuses) && semStatuses.length > 0) {
              const locked = semStatuses.find((s) => !s.unlocked);
              chosenSem = locked ? locked.semester : (semStatuses[0]?.semester || 1);
            }

            navigate(buildTargetUrl(chosenSem || 1, chosenStream || "common"));
          })
          .catch(() => {
            navigate(buildTargetUrl(semParam || 1, streamParam || "common"));
          });
      } else {
        navigate(buildTargetUrl(semParam || 1, streamParam || "common"));
      }
    },
    [isAdminPreview, isAuthenticated, tokens?.accessToken, navigate],
  );

  useEffect(() => {
    const handlePremiumRequired = (event?: Event) => {
      if (!isAdminPreview) {
        const customEvt = event as CustomEvent<OpenUpgradeOptions>;
        openUpgrade(customEvt?.detail);
      }
    };
    window.addEventListener("soorgaa:premium_required", handlePremiumRequired);
    return () => {
      window.removeEventListener("soorgaa:premium_required", handlePremiumRequired);
    };
  }, [isAdminPreview, openUpgrade]);

  const value = useMemo<StudentContextValue>(
    () => ({
      // Admins get full student features with no upgrade gates.
      isPremium: isAdminPreview || isPremiumFlag,
      setIsPremium,
      showUpgrade: false,
      openUpgrade,
      closeUpgrade: () => {},
      isAdminPreview,
      semesterStatuses,
      refreshSemesterStatus,
      getSemesterStatus,
      hasPendingRequest,
    }),
    [
      isAdminPreview,
      isPremiumFlag,
      openUpgrade,
      semesterStatuses,
      refreshSemesterStatus,
      getSemesterStatus,
      hasPendingRequest,
    ],
  );

  return (
    <StudentContext.Provider value={value}>
      {children}
    </StudentContext.Provider>
  );
}

export function useStudent() {
  const ctx = useContext(StudentContext);
  if (!ctx) throw new Error("useStudent must be used within StudentProvider");
  return ctx;
}

export function useOptionalStudent() {
  return useContext(StudentContext);
}

